import { ref } from "vue";

export type Platform = "telegram" | "max" | "vk" | "unknown";

const platform = ref<Platform>("unknown");

function getHashParams(): URLSearchParams {
  return new URLSearchParams(window.location.hash.replace(/^#\/?/, ""));
}

function detectPlatform(): Platform {
  const hashParams = getHashParams();
  const url = new URL(window.location.href);

  if (hashParams.has("WebAppVersion") || url.searchParams.has("WebAppVersion"))
    return "max";

  const webApp = (window as any).WebApp;
  if (webApp?.initData || webApp?.version) return "max";
  if (
    process.env.VUE_APP_FEATURE_TELEGRAM === "true" &&
    (window as any).Telegram?.WebApp?.initData
  )
    return "telegram";
  if (url.searchParams.has("vk_user_id") && url.searchParams.has("sign"))
    return "vk";

  return "unknown";
}

function waitForObject(
  check: () => boolean,
  timeoutMs = 500,
  intervalMs = 50,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (check()) return resolve(true);
    const interval = setInterval(() => {
      if (check()) {
        cleanup();
        resolve(true);
      }
    }, intervalMs);
    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);
    const cleanup = () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  });
}

function loadScript(src: string, timeoutMs = 3000): Promise<void> {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();

    const script = Object.assign(document.createElement("script"), {
      src,
      async: true,
    });
    const cleanup = () => {
      clearTimeout(timer);
      script.onload = script.onerror = null;
      resolve();
    };
    const timer = setTimeout(cleanup, timeoutMs);
    script.onload = cleanup;
    script.onerror = cleanup;
    document.head.appendChild(script);
  });
}

export async function initBridge(): Promise<{
  platform: Platform;
  initData: string | null;
}> {
  const urlParams = new URL(window.location.href).searchParams;

  if (urlParams.has("vk_user_id") && urlParams.has("sign")) {
    await loadScript(
      "https://unpkg.com/@vkontakte/vk-bridge/dist/browser.min.js",
    );
    (window as any).vkBridge?.send("VKWebAppInit");
    platform.value = "vk";
    return { platform: "vk", initData: window.location.search };
  }

  const isMax =
    getHashParams().has("WebAppVersion") ||
    urlParams.has("WebAppVersion") ||
    sessionStorage.getItem("WebAppVersion") !== null;

  if (isMax) {
    await loadScript("https://st.max.ru/js/max-web-app.js");
  } else if (process.env.VUE_APP_FEATURE_TELEGRAM === "true") {
    await loadScript("https://telegram.org/js/telegram-web-app.js");
  } else if (
    !(window as any).WebApp?.initData &&
    !(window as any).WebApp?.version
  ) {
    platform.value = "unknown";
    return { platform: "unknown", initData: null };
  }

  await waitForObject(() => {
    if (isMax) {
      return (
        !!(window as any).WebApp?.initData ||
        !!(window as any).WebApp?.version ||
        window.location.search.includes("WebAppVersion") ||
        window.location.hash.includes("WebAppVersion")
      );
    }
    if (process.env.VUE_APP_FEATURE_TELEGRAM === "true") {
      return !!(
        (window as any).Telegram?.WebApp?.initData ||
        (window as any).WebApp?.initData
      );
    }
    return !!(
      (window as any).WebApp?.initData ||
      (window as any).WebApp?.version
    );
  });

  const detected = detectPlatform();
  platform.value = detected;

  if (process.env.VUE_APP_FEATURE_TELEGRAM === "true" && detected === "telegram") {
    const tg = (window as any).Telegram.WebApp;
    tg.ready();
    return { platform: "telegram", initData: tg.initData || null };
  }

  if (detected === "max") {
    const webApp = (window as any).WebApp;
    webApp.ready();
    return { platform: "max", initData: webApp.initData || null };
  }

  return { platform: "unknown", initData: null };
}

export function getPlatform(): Platform {
  return platform.value;
}
